import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/api";
import { scrollToContact } from "@/components/site/Chrome";
import { ContactSection } from "@/components/site/Contact";
import { PropertyCard } from "@/components/site/Cards";
import { useCollection, usePageMeta } from "@/hooks/useData";

const SLIDES = [
  { img: IMAGES.hero1, bg: "bg-gw-slide1", cap: "01 · Grey Wolfe Limited", alt: "Offshore supply vessel at sea" },
  { img: IMAGES.hero2, bg: "bg-gw-slide2", cap: "02 · Wolfe Realtors", alt: "Luxury residence living space" },
  { img: IMAGES.hero3, bg: "bg-gw-slide3", cap: "03 · Grey Wolfe Development", alt: "Tower under construction with cranes" },
];

const ARMS = [
  { to: "/oil-gas", n: "Page I", name: "Grey Wolfe Limited", cta: "Open oil & gas", id: "oil-gas", desc: "Survey, procurement and marine vessel supply." },
  { to: "/realty", n: "Page II", name: "Wolfe Realtors", cta: "Open realty", id: "realty", desc: "Brokerage, sales, leasing and investment advisory." },
  { to: "/development", n: "Page III", name: "Grey Wolfe Development", cta: "Open development", id: "development", desc: "Projects we build — come and see them." },
];

const MARQUEE = ["Survey", "Procurement", "Marine Vessel Supply", "Brokerage", "Sales", "Leasing", "Investment Advisory", "Build"];

const Hero = () => (
  <section data-testid="home-hero" className="relative isolate overflow-hidden bg-gw-deep text-white min-h-[620px] md:min-h-[88vh] flex items-end">
    <div className="absolute inset-0">
      {SLIDES.map((s) => (
        <figure key={s.cap} className="xf absolute inset-0 overflow-hidden m-0">
          <div className={`kb absolute inset-0 ${s.bg}`}>
            <img src={s.img} alt={s.alt} className="w-full h-full object-cover" />
          </div>
          <figcaption className="absolute top-10 right-6 lab text-gw-pale">{s.cap}</figcaption>
        </figure>
      ))}
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
    <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16 pt-48 w-full">
      <p className="lab text-gw-mist mb-4">Oil &amp; gas · Realty · Development</p>
      <h1 className="lux gw-h1" data-testid="home-hero-heading">
        <span className="ln"><span>From seabed</span></span>
        <span className="ln"><span>to skyline.</span></span>
      </h1>
      <p className="fu fu-sub mt-6 gw-body max-w-lg" data-testid="home-hero-differentiator">
        One group handles everything: build, sell, lease and manage.
      </p>
      <div className="fu fu-cta mt-8 flex flex-wrap gap-4 items-center">
        <button type="button" onClick={scrollToContact} data-testid="home-hero-enquire-button"
          className="group lab bg-white text-gw-onwhite px-6 py-4 inline-flex gap-3 transition-colors duration-200 hover:bg-gw-mist">
          Send an enquiry <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
        </button>
        <Link to="/realty" data-testid="home-hero-properties-link" className="lab border border-white/60 px-6 py-4 transition-colors duration-200 hover:bg-white/10">
          View properties
        </Link>
        <span className="flex gap-2 ml-2" aria-hidden="true">
          {SLIDES.map((s) => <i key={s.cap} className="dot w-2 h-2 rounded-full bg-white" />)}
        </span>
      </div>
    </div>
  </section>
);

const Arms = () => (
  <section aria-label="Our three companies" className="bg-gw-paper text-gw-ink py-16 md:py-24">
    <div className="max-w-6xl mx-auto px-6">
      <p className="lab mb-6 text-gw-accent">Choose a company to open its page</p>
      <div className="grid md:grid-cols-3 gap-6">
        {ARMS.map((a) => (
          <Link key={a.to} to={a.to} data-testid={`home-arm-${a.id}-card`}
            className="pg group block border border-gw-rule/30 p-6 md:p-8 transition-colors duration-300 hover:bg-gw-hover">
            <p className="lab">{a.n}</p>
            <p className="lux text-3xl mt-3">{a.name}</p>
            <p className="text-sm mt-4 text-gw-accent/80">{a.desc}</p>
            <p className="lab mt-8 text-gw-accent">{a.cta} <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span></p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const Marquee = () => (
  <div className="bg-gw-deep text-white overflow-hidden py-6 border-y border-white/20" data-testid="services-marquee">
    <div className="mq lux text-3xl">
      {[0, 1].map((k) => (
        <span key={k} className="flex gap-12 pr-12" aria-hidden={k === 1}>
          {MARQUEE.map((m) => <span key={m}>{m} ·</span>)}
        </span>
      ))}
    </div>
  </div>
);

const LatestTeaser = () => {
  const props = useCollection("properties");
  const list = (props || []).slice(0, 3);
  if (!list.length) return null;
  return (
    <section className="bg-gw-paper2 text-gw-ink py-24 md:py-32" data-testid="home-latest-properties">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="lab text-gw-accent">Wolfe Realtors</p>
            <h2 className="lux gw-h2 mt-4">Latest properties</h2>
          </div>
          <Link to="/realty" data-testid="home-all-properties-link" className="group lab text-gw-accent inline-flex gap-2">
            All properties <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
          </Link>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {list.map((p, i) => <PropertyCard key={p.id} p={p} large={i === 0} idx={i} />)}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section id="testimonials" className="bg-gw-paper text-gw-ink py-24 md:py-32" data-testid="testimonials-section">
    <div className="max-w-6xl mx-auto px-6">
      <p className="gw-big lux text-gw-accent" aria-hidden="true">&ldquo;</p>
      <h2 className="lux gw-h2 mt-4">In their words</h2>
      <p className="mt-4 text-gw-accent/70">Notes from our clients and partners will appear here.</p>
    </div>
  </section>
);

export default function Home() {
  usePageMeta("Grey Wolfe — Oil & Gas, Realty and Real Estate Development",
    "Grey Wolfe group in Nigeria: oil & gas survey, procurement and marine vessel supply, Wolfe Realtors property sales and leasing, and real estate development.");
  return (
    <div className="page-in">
      <Hero />
      <Arms />
      <Marquee />
      <LatestTeaser />
      <Testimonials />
      <ContactSection />
    </div>
  );
}
